import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Spin, Steps } from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { LeftOutlined } from '@ant-design/icons'
import { useAuthSession } from '@/lib/session'
import {
  useComplianceAssets,
  useComplianceJobStatus,
  useReanalyzeJobMutation,
  useTrialBalanceAvailability,
  useUploadJobFilesMutation,
  useXeroTrialBalance,
} from '@/features/compliance/hooks'
import {
  AiAnalysisStep,
  ExportReportStep,
  ReviewResultsStep,
  UploadFilesStep,
} from './components'
import type { TbSource } from './components/TrialBalancePanel'
import { getTrialBalanceAssets } from '@/features/compliance/utils'
import { useToast } from '@/contexts/ToastContext'
import { AssetReviewStatus, ComplianceJobStatus } from '@/features/compliance/types'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'

const STEP_UPLOAD = 0
const STEP_AI_ANALYSIS = 1
const STEP_REVIEW = 2
const STEP_EXPORT = 3

export default function JobPage() {
  const { id: orgId = '', jobId = '' } = useParams<{ id: string; jobId: string }>()
  const { addToast } = useToast()
  const session = useAuthSession()
  const clientId = session?.currentClientId || ''

  const [currentStep, setCurrentStep] = useState(0)
  const [indexFileList, setIndexFileList] = useState<UploadFile[]>([])
  const [workpaperFileList, setWorkpaperFileList] = useState<UploadFile[]>([])
  const [isReuploadMode, setIsReuploadMode] = useState(false)
  const [isReviewLocked, setIsReviewLocked] = useState(false)
  const [tbSource, setTbSourceState] = useState<TbSource>(() => {
    const stored = localStorage.getItem(`compliance-tb-source-${jobId}`)
    return (stored as TbSource) ?? 'upload'
  })
  const initialStepSet = useRef(false)

  const setTbSource = (source: TbSource) => {
    setTbSourceState(source)
    localStorage.setItem(`compliance-tb-source-${jobId}`, source)
  }

  const orgsQuery = useTrialBalanceAvailability(clientId)
  const companyId = orgsQuery.data?.find((p) => p.platformId === orgId)?.companyId ?? ''
  const xeroTbQuery = useXeroTrialBalance(clientId, jobId)
  const statusQuery = useComplianceJobStatus(clientId, jobId)
  const assetsQuery = useComplianceAssets(clientId, jobId)
  const uploadJobFilesMutation = useUploadJobFilesMutation(clientId)
  const reanalyzeJobMutation = useReanalyzeJobMutation(clientId)
  const isBusyUploading = uploadJobFilesMutation.isPending || reanalyzeJobMutation.isPending

  const xeroTbRows = xeroTbQuery.data ?? []
  const assets = getTrialBalanceAssets(xeroTbRows)
  const hasXeroAssets = assets.length > 0
  const complianceAssets = assetsQuery.data ?? []
  const hasFlaggedItems = complianceAssets.some(
    (asset) => asset.reviewStatus === AssetReviewStatus.NeedsReview
  )
  const allAssetsReviewed =
    complianceAssets.length > 0 &&
    complianceAssets.every((asset) => asset.reviewStatus === AssetReviewStatus.Reviewed)
  const isJobReviewed = statusQuery.data?.status === ComplianceJobStatus.Reviewed
  const isReuploadBlocked = isReviewLocked || isJobReviewed

  const steps = [
    { title: 'Upload Files' },
    { title: 'AI Analysis' },
    { title: 'Review Results' },
    { title: 'Export Report' },
  ]

  useEffect(() => {
    if (initialStepSet.current || !statusQuery.data?.status) return
    initialStepSet.current = true
    const status = statusQuery.data.status
    if (status === ComplianceJobStatus.Pending) setCurrentStep(STEP_UPLOAD)
    else if (status === ComplianceJobStatus.Processing || status === ComplianceJobStatus.Reviewing) setCurrentStep(STEP_AI_ANALYSIS)
    else if (status === ComplianceJobStatus.Parsed || status === ComplianceJobStatus.NeedsReview) setCurrentStep(STEP_REVIEW)
    else if (status === ComplianceJobStatus.Reviewed) setCurrentStep(STEP_EXPORT)
  }, [statusQuery.data?.status])

  useEffect(() => {
    if (statusQuery.data?.status === ComplianceJobStatus.Reviewed) {
      setIsReviewLocked(true)
    }
  }, [statusQuery.data?.status])

  useEffect(() => {
    if (!isBusyUploading) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isBusyUploading])

  const getReuploadBlockedMessage = () => {
    if (isReuploadBlocked) {
      return 'Once a job is reviewed, re-upload is no longer allowed'
    }
    if (allAssetsReviewed) {
      return 'Re-upload is unavailable once all assets are reviewed'
    }
    return 'Re-upload is only available for flagged items'
  }

  const handleUploadWorkpapers = async () => {
    const workpaperFiles = workpaperFileList
      .map((f) => f.originFileObj)
      .filter((f): f is NonNullable<typeof f> => f != null) as File[]
    if (workpaperFiles.length === 0) {
      addToast('Add at least one workpaper file', 'warning')
      return
    }

    if (isReuploadMode) {
      if (isReuploadBlocked || allAssetsReviewed) {
        addToast(getReuploadBlockedMessage(), 'warning')
        setIsReuploadMode(false)
        return
      }
      if (!hasFlaggedItems) {
        addToast(getReuploadBlockedMessage(), 'warning')
        setIsReuploadMode(false)
        return
      }
      try {
        await reanalyzeJobMutation.mutateAsync({
          jobId,
          workpaperFiles,
          orgId: tbSource === 'xero' ? companyId || undefined : undefined,
        })
        addToast('Re-analyzing flagged items with new workpapers', 'success')
        setIsReuploadMode(false)
        setWorkpaperFileList([])
        setCurrentStep(STEP_AI_ANALYSIS)
      } catch (e) {
        addToast(e instanceof Error ? e.message : 'Failed to re-analyze', 'error')
      }
      return
    }

    const trialBalanceSource = tbSource === 'xero' ? 'Xero' : 'FileUpload'

    if (tbSource === 'xero') {
      if (!hasXeroAssets) {
        addToast('Select an organisation with Xero trial balance data', 'warning')
        return
      }
      try {
        await uploadJobFilesMutation.mutateAsync({
          jobId,
          indexFile: null,
          workpaperFiles,
          orgId: companyId || undefined,
          trialBalanceSource,
        })
        addToast('Workpapers uploaded', 'success')
        setWorkpaperFileList([])
        setCurrentStep(STEP_AI_ANALYSIS)
      } catch (e) {
        addToast(e instanceof Error ? e.message : 'Failed to upload workpapers', 'error')
      }
    } else {
      const indexFiles = indexFileList
        .map((f) => f.originFileObj)
        .filter((f): f is NonNullable<typeof f> => f != null) as File[]
      if (indexFiles.length === 0) {
        addToast('Upload a trial balance file', 'warning')
        return
      }
      try {
        await uploadJobFilesMutation.mutateAsync({
          jobId,
          indexFile: indexFiles[0],
          workpaperFiles,
          orgId: undefined,
          trialBalanceSource,
        })
        addToast('Files uploaded', 'success')
        setWorkpaperFileList([])
        setCurrentStep(STEP_AI_ANALYSIS)
      } catch (e) {
        addToast(e instanceof Error ? e.message : 'Failed to upload files', 'error')
      }
    }
  }

  const uploadError =
    xeroTbQuery.isError || statusQuery.isError
      ? (xeroTbQuery.error as Error)?.message ||
        (statusQuery.error as Error)?.message ||
        'Failed to load job data'
      : null

  if (xeroTbQuery.isLoading && !xeroTbQuery.data) {
    return (
      <FeatureThemeProvider
        featureKey="complianceIntel"
        withAntd
        className="feature-panel-soft-strong flex min-h-[200px] items-center justify-center rounded-2xl"
      >
        <Spin size="large" />
      </FeatureThemeProvider>
    )
  }

  return (
    <FeatureThemeProvider
      featureKey="complianceIntel"
      withAntd
      className="feature-shell flex min-h-0 flex-col p-6"
    >
      <div className="mb-6 shrink-0">
        <Link
          to={orgId ? `/annual-compliance?org=${encodeURIComponent(orgId)}` : '/annual-compliance'}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <LeftOutlined /> Back to CompVerify jobs
        </Link>
      </div>
      <div className="mb-8 shrink-0">
        <Steps
          current={currentStep}
          items={steps.map((s) => ({ title: s.title }))}
        />
      </div>
      <div className="min-h-0 flex-1">
        {currentStep === STEP_UPLOAD && (
          <UploadFilesStep
            hasXeroAssets={hasXeroAssets}
            assets={assets}
            indexFileList={indexFileList}
            onIndexFileListChange={setIndexFileList}
            workpaperFileList={workpaperFileList}
            onWorkpaperFileListChange={setWorkpaperFileList}
            onStartAnalysis={handleUploadWorkpapers}
            isUploading={isBusyUploading}
            isLoadingAssets={assetsQuery.isLoading}
            error={uploadError}
            tbSource={tbSource}
            onTbSourceChange={setTbSource}
            isReuploadMode={isReuploadMode}
            reuploadMessage={
              isReuploadMode
                ? 'Re-upload is limited to workpapers for flagged items. Your existing trial balance source and index file will be reused.'
                : undefined
            }
            disableTrialBalanceControls={isReuploadMode}
            uploadMessage={
              isReuploadMode
                ? 'Uploading replacement workpapers and re-running analysis for flagged items. Please keep this window open.'
                : 'Uploading files, validating them, and starting AI analysis. Please keep this window open.'
            }
          />
        )}
        {currentStep === STEP_AI_ANALYSIS && (
          <AiAnalysisStep
            clientId={clientId}
            jobId={jobId}
            onNext={() => setCurrentStep(STEP_REVIEW)}
          />
        )}
        {currentStep === STEP_REVIEW && (
          <ReviewResultsStep
            clientId={clientId}
            jobId={jobId}
            onNext={() => setCurrentStep(STEP_EXPORT)}
            onBack={() => {
              setIsReuploadMode(false)
              setCurrentStep(STEP_UPLOAD)
            }}
            onAddFlaggedWorkpapers={() => {
              if (isReuploadBlocked || allAssetsReviewed) {
                addToast(getReuploadBlockedMessage(), 'warning')
                return
              }
              if (!hasFlaggedItems) {
                addToast(getReuploadBlockedMessage(), 'warning')
                return
              }
              setIsReuploadMode(true)
              setWorkpaperFileList([])
              setCurrentStep(STEP_UPLOAD)
            }}
            disableReupload={isReuploadBlocked || allAssetsReviewed || !hasFlaggedItems}
            onJobStatusChange={(jobStatus) => {
              if (jobStatus === ComplianceJobStatus.Reviewed) {
                setIsReviewLocked(true)
              }
            }}
          />
        )}
        {currentStep === STEP_EXPORT && (
          <ExportReportStep
            clientId={clientId}
            jobId={jobId}
            orgId={orgId}
            onBack={() => setCurrentStep(STEP_REVIEW)}
          />
        )}
      </div>
    </FeatureThemeProvider>
  )
}
